const BASIS_POINTS_SCALE = 10_000

/** Fixed, platform-owned commercial policy for newly eligible invoices. */
export const TIMEBANKEN_PLATFORM_FEE_POLICY = Object.freeze({
  basisPoints: 100,
  policyVersion: 1,
  vatInclusive: true,
  vatBasisPoints: 2_500,
} as const)

export const TIMEBANKEN_PLATFORM_FEE_BASIS_POINTS =
  TIMEBANKEN_PLATFORM_FEE_POLICY.basisPoints
export const TIMEBANKEN_PLATFORM_FEE_POLICY_VERSION =
  TIMEBANKEN_PLATFORM_FEE_POLICY.policyVersion
export const TIMEBANKEN_PLATFORM_FEE_VAT_BASIS_POINTS =
  TIMEBANKEN_PLATFORM_FEE_POLICY.vatBasisPoints

export interface PlatformFeeVatSplit {
  grossAmount: number
  netAmount: number
  vatAmount: number
}

function validateMinorUnitAmount(amount: number, name: string): void {
  if (!Number.isSafeInteger(amount) || amount < 0) {
    throw new RangeError(`${name} must be a non-negative safe integer`)
  }
}

function validateBasisPoints(basisPoints: number, name: string): void {
  if (
    !Number.isInteger(basisPoints) ||
    basisPoints < 0 ||
    basisPoints >= BASIS_POINTS_SCALE
  ) {
    throw new RangeError(`${name} must be an integer from 0 through 9,999`)
  }
}

/** Rounds a non-negative integer ratio exactly using half-up rounding. */
function multiplyAndRoundRatio(
  amount: number,
  numerator: number,
  denominator: number,
): number {
  const integerAmount = BigInt(amount)
  const integerNumerator = BigInt(numerator)
  const integerDenominator = BigInt(denominator)
  const rounded =
    (integerAmount * integerNumerator + integerDenominator / BigInt(2)) /
    integerDenominator
  return Number(rounded)
}

/** Calculates the VAT-inclusive Timebanken fee in minor currency units. */
export function calculatePlatformFeeAmount(
  grossAmount: number,
  feeBasisPoints: number = TIMEBANKEN_PLATFORM_FEE_BASIS_POINTS,
): number {
  validateMinorUnitAmount(grossAmount, "grossAmount")
  validateBasisPoints(feeBasisPoints, "feeBasisPoints")

  if (grossAmount === 0 || feeBasisPoints === 0) return 0

  const roundedFee = multiplyAndRoundRatio(
    grossAmount,
    feeBasisPoints,
    BASIS_POINTS_SCALE,
  )

  // Stripe requires an application fee to be lower than the invoice total.
  return Math.min(roundedFee, grossAmount - 1)
}

/** Splits a VAT-inclusive platform fee into net revenue and output VAT. */
export function calculatePlatformFeeVatSplit(
  grossAmount: number,
  vatBasisPoints: number = TIMEBANKEN_PLATFORM_FEE_VAT_BASIS_POINTS,
): PlatformFeeVatSplit {
  validateMinorUnitAmount(grossAmount, "grossAmount")
  validateBasisPoints(vatBasisPoints, "vatBasisPoints")

  const vatAmount = multiplyAndRoundRatio(
    grossAmount,
    vatBasisPoints,
    BASIS_POINTS_SCALE + vatBasisPoints,
  )

  return {
    grossAmount,
    netAmount: grossAmount - vatAmount,
    vatAmount,
  }
}
